/**
 * Connecting a source takes the browser away to the provider's consent screen, and the API sends it
 * back here afterwards. This is the marker that says the trip happened, so the settings page can
 * scroll back to the section it was left in rather than dropping you at the top.
 */
const RETURN_PARAM = 'philBotConnected';

/** Where the API should send the browser back to once the source has been authorised. */
export const philBotReturnUrl = (): string => {
  const url = new URL(window.location.href);
  url.searchParams.set(RETURN_PARAM, '1');
  return url.toString();
};

export const hasPhilBotReturn = (): boolean => new URL(window.location.href).searchParams.has(RETURN_PARAM);

/** Drops the marker from the address bar, so a refresh does not act on the trip a second time. */
export const clearPhilBotReturn = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete(RETURN_PARAM);
  window.history.replaceState({}, '', url);
};

// In the hash, not the query: Google and Slack send a declined consent back with the query cut off.
const POPUP_HASH = '#phill-connect-popup';
// Not `window.opener`: the provider's pages and the API's send a COOP header, which cuts the popup off from it.
const CONNECT_CHANNEL = 'phill-connect';

/** Where a connect popup comes back to: this page's path, marked so the popup closes instead of loading it. */
export const connectPopupReturnUrl = (): string => `${window.location.origin}${window.location.pathname}${POPUP_HASH}`;

/** True when this window is a connect popup that came back: it tells the form, then tries to close. */
export const closeIfConnectPopup = (): boolean => {
  if (window.location.hash !== POPUP_HASH) return false;
  const channel = new BroadcastChannel(CONNECT_CHANNEL);
  channel.postMessage('connected');
  channel.close();
  window.close();
  return true;
};

/** Calls `listener` whenever a connect popup comes back; returns the unsubscribe. */
export const onConnectPopupBack = (listener: () => void): (() => void) => {
  const channel = new BroadcastChannel(CONNECT_CHANNEL);
  channel.onmessage = () => listener();
  return () => channel.close();
};
